import { cn } from '@/lib/utils'
import type { ClassValue } from 'class-variance-authority/types'

type VariantGroups = {
  [variantName: string]: Record<string, ClassValue>
}

type CvaConfig<VG extends VariantGroups> = {
  variants: VG
  defaultVariants?: {
    [K in keyof VG]?: K extends 'fullWidth' ? boolean : keyof VG[K]
  }
}

type CvaVariants<VG extends VariantGroups> = {
  baseClass: ClassValue
  config: CvaConfig<VG>
}

export function defineCvaVariants<VG extends VariantGroups>(
  cvaVariants: CvaVariants<VG>
): CvaVariants<VG> {
  return cvaVariants
}

type MergeVariantGroups<
  VG1 extends VariantGroups,
  VG2 extends VariantGroups
> = {
  [K in keyof VG1 | keyof VG2]: K extends keyof VG1
    ? K extends keyof VG2
      ? VG1[K] & VG2[K]
      : VG1[K]
    : K extends keyof VG2
    ? VG2[K]
    : never
}

type DefaultVariants<VG extends VariantGroups> = {
  [K in keyof VG]?: K extends 'fullWidth' ? boolean : keyof VG[K]
}

export function extendCvaVariants<
  VG1 extends VariantGroups,
  VG2 extends VariantGroups,
  MergedVG extends MergeVariantGroups<VG1, VG2> = MergeVariantGroups<VG1, VG2>
>(base: CvaVariants<VG1>, extend: CvaVariants<VG2>): CvaVariants<MergedVG> {
  const mergedVariants = {} as MergedVG

  const allKeys = Array.from(
    new Set([
      ...Object.keys(base.config.variants),
      ...Object.keys(extend.config.variants),
    ])
  ) as (keyof MergedVG)[]

  for (const key of allKeys) {
    const baseGroup = base.config.variants[key as keyof VG1] ?? {}
    const extendGroup = extend.config.variants[key as keyof VG2] ?? {}

    mergedVariants[key] = {
      ...baseGroup,
      ...extendGroup,
    } as MergedVG[typeof key]
  }

  return {
    baseClass: cn(base.baseClass, extend.baseClass),
    config: {
      variants: mergedVariants,
      defaultVariants: {
        ...(base.config.defaultVariants ?? {}),
        ...(extend.config.defaultVariants ?? {}),
      } as DefaultVariants<MergedVG>,
    },
  }
}
